import clientsData from '../data/clients.json';
import offersData from '../data/offers.json';
import reviewsData from '../data/reviews.json';
import appointmentsData from '../data/appointments.json';

export interface Client {
  id: string;
  name: string;
  dateOfBirth: string;
  phone: string;
  email: string;
  lastVisit: {
    date: string;
    services: string[];
    stylist: string;
    totalCost: number;
    notes?: string;
  };
  visitHistory: Array<{
    date: string;
    services: string[];
    stylist: string;
    totalCost: number;
  }>;
}

export interface Offer {
  id: string;
  title: string;
  description: string;
  discount?: number | null;
  originalPrice?: number | null;
  discountedPrice?: number | null;
  fixedDiscount?: number | null;
  validFrom: string;
  validUntil: string;
  isActive: boolean;
  services: string[];
  restrictions?: string;
}

export interface Review {
  id: string;
  clientId: string;
  clientName: string;
  visitDate: string;
  rating: number;
  review: string;
  stylist: string;
  services: string[];
  dateSubmitted: string;
  recommended: boolean;
}

export interface Appointment {
  id: string;
  clientId: string;
  clientName: string;
  date: string;
  time: string;
  services: string[];
  stylist: string;
  estimatedDuration: number;
  estimatedCost: number;
  status: 'confirmed' | 'pending' | 'cancelled';
  notes?: string;
  dateBooked: string;
  selectedOffer?: {
    offerId: string;
    offerTitle: string;
    originalCost: number;
    discountAmount: number;
    finalCost: number;
  };
}

export interface CallbackSchedule {
  id: string;
  clientId: string;
  clientName: string;
  callbackDate: string;
  callbackTime: string;
  reason: 'not_interested' | 'busy' | 'reschedule' | 'other';
  notes?: string;
  preferredMethod: 'phone' | 'email' | 'either';
  dateScheduled: string;
  status: 'pending' | 'completed' | 'cancelled';
}

export const fetchClientDetails = (name: string, dateOfBirth: string): Client | null => {
  console.log("clientsDataFETCHING", name, dateOfBirth);
  const client = clientsData.find(client => 
    client.name.toLowerCase() === name.toLowerCase() && 
    client.dateOfBirth === dateOfBirth
  );
  return client || null;
};

export const fetchLatestOffers = (): Offer[] => {
  const now = new Date();
  return offersData.filter(offer => {
    const validFrom = new Date(offer.validFrom);
    const validUntil = new Date(offer.validUntil);
    return offer.isActive && now >= validFrom && now <= validUntil;
  });
};

export const fetchAllOffers = (): Offer[] => {
  return offersData;
};

export const saveReview = (review: Omit<Review, 'id' | 'dateSubmitted'>): Review => {
  const newReview: Review = {
    ...review,
    id: `review_${Date.now()}`,
    dateSubmitted: new Date().toISOString().split('T')[0],
  };
  
  // In a real application, this would save to a database
  // For now, we'll just return the review object
  console.log('Saving review:', newReview);
  return newReview;
};

// --- LocalStorage Utilities for Appointments ---
const APPOINTMENTS_STORAGE_KEY = 'salon_appointments';

function loadAppointmentsFromStorage(): Appointment[] {
  try {
    const stored = localStorage.getItem(APPOINTMENTS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Failed to load appointments from localStorage:', e);
  }
  return appointmentsData as Appointment[];
}

function saveAppointmentsToStorage(appointments: Appointment[]) {
  try {
    localStorage.setItem(APPOINTMENTS_STORAGE_KEY, JSON.stringify(appointments));
  } catch (e) {
    console.warn('Failed to save appointments to localStorage:', e);
  }
}

// --- Use localStorage-backed appointments ---
export function getAllAppointments(): Appointment[] {
  return loadAppointmentsFromStorage();
}

export const bookAppointment = (appointmentData: Omit<Appointment, 'id' | 'dateBooked' | 'status'>, selectedOfferId?: string): Appointment => {
  let finalAppointmentData = { ...appointmentData };
  
  // Apply offer if selected
  if (selectedOfferId) {
    const offerApplication = applyOfferToBooking(
      appointmentData.estimatedCost, 
      selectedOfferId, 
      appointmentData.services
    );
    
    if (offerApplication) {
      finalAppointmentData.selectedOffer = {
        offerId: selectedOfferId,
        offerTitle: offerApplication.offerTitle,
        originalCost: offerApplication.originalCost,
        discountAmount: offerApplication.discountAmount,
        finalCost: offerApplication.finalCost
      };
      // Update the estimated cost to reflect the discount
      finalAppointmentData.estimatedCost = offerApplication.finalCost;
    }
  }
  
  const newAppointment: Appointment = {
    ...finalAppointmentData,
    id: `appt_${Date.now()}`,
    dateBooked: new Date().toISOString().split('T')[0],
    status: 'confirmed',
  };
  
  // Save to localStorage
  const current = loadAppointmentsFromStorage();
  const updated = [...current, newAppointment];
  saveAppointmentsToStorage(updated);
  console.log('Booking appointment:', newAppointment);
  return newAppointment;
};

export const getAvailableTimeSlots = (date: string, stylist?: string): string[] => {
  // In a real application, this would check actual availability
  // For now, return some sample time slots
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
  ];
  
  // Use appointments from localStorage
  const allAppointments = loadAppointmentsFromStorage();
  const existingBookings = allAppointments
    .filter(appt => appt.date === date && (!stylist || appt.stylist === stylist))
    .map(appt => appt.time);
  
  return timeSlots.filter(slot => !existingBookings.includes(slot));
};

export const formatLastVisit = (client: Client): string => {
  const lastVisit = client.lastVisit;
  return `Your last visit was on ${lastVisit.date} with ${lastVisit.stylist}. You had ${lastVisit.services.join(', ')} for a total of $${lastVisit.totalCost}. ${lastVisit.notes || ''}`;
};

export const formatOffers = (offers: Offer[]): string => {
  if (offers.length === 0) {
    return "We don't have any special offers running right now, but we'd love to help you with our regular services!";
  }
  
  return offers.map(offer => {
    let description = `${offer.title}: ${offer.description}`;
    if (offer.discount) {
      description += ` Save ${offer.discount}% (was $${offer.originalPrice}, now $${offer.discountedPrice})`;
    } else if (offer.fixedDiscount) {
      description += ` Save $${offer.fixedDiscount}`;
    }
    if (offer.restrictions) {
      description += ` *${offer.restrictions}*`;
    }
    return description;
  }).join('\n\n');
};

export const validateOffer = (offerId: string, services?: string[]): {
  isValid: boolean;
  reason?: string;
  offer?: Offer;
} => {
  const offer = offersData.find(o => o.id === offerId);
  
  if (!offer) {
    return { isValid: false, reason: "Offer not found" };
  }
  
  if (!offer.isActive) {
    return { isValid: false, reason: "This offer is no longer active", offer };
  }
  
  // Check if offer is currently valid by date
  const now = new Date();
  const validFrom = new Date(offer.validFrom);
  const validUntil = new Date(offer.validUntil);
  
  if (now < validFrom) {
    return { 
      isValid: false, 
      reason: `This offer is not yet available. It starts on ${validFrom.toLocaleDateString()}`, 
      offer 
    };
  }
  
  if (now > validUntil) {
    return { 
      isValid: false, 
      reason: `This offer has expired. It was valid until ${validUntil.toLocaleDateString()}`, 
      offer 
    };
  }
  
  // Check if services qualify for the offer (if services provided)
  if (services && services.length > 0) {
    const qualifiesForOffer = offer.services.includes("All Services") || 
      offer.services.some(offerService => services.includes(offerService));
    
    if (!qualifiesForOffer) {
      return { 
        isValid: false, 
        reason: `This offer doesn't apply to your selected services. It's valid for: ${offer.services.join(', ')}`, 
        offer 
      };
    }
  }
  
  return { isValid: true, offer };
};

export const applyOfferToBooking = (originalCost: number, offerId: string, services: string[]): {
  originalCost: number;
  discountAmount: number;
  finalCost: number;
  offerTitle: string;
} | null => {
  const validation = validateOffer(offerId, services);
  
  if (!validation.isValid || !validation.offer) {
    return null;
  }
  
  const offer = validation.offer;
  let discountAmount = 0;
  
  if (offer.discount) {
    // Percentage discount
    discountAmount = (originalCost * offer.discount) / 100;
  } else if (offer.fixedDiscount) {
    // Fixed amount discount
    discountAmount = Math.min(offer.fixedDiscount, originalCost);
  } else if (offer.discountedPrice && offer.originalPrice) {
    // Package deal with fixed price
    if (originalCost >= offer.originalPrice) {
      discountAmount = originalCost - offer.discountedPrice;
    }
  }
  
  const finalCost = Math.max(0, originalCost - discountAmount);
  
  return {
    originalCost,
    discountAmount,
    finalCost,
    offerTitle: offer.title
  };
};

export const scheduleCallback = (callbackData: Omit<CallbackSchedule, 'id' | 'dateScheduled' | 'status'>): CallbackSchedule => {
  const newCallback: CallbackSchedule = {
    ...callbackData,
    id: `callback_${Date.now()}`,
    dateScheduled: new Date().toISOString().split('T')[0],
    status: 'pending',
  };
  
  // In a real application, this would save to a database
  // For now, we'll just return the callback object and log it
  console.log('Scheduling callback:', newCallback);
  return newCallback;
}; 